/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import { useNavigate } from 'react-router'
import { AclLevel, FsiType } from '../../constants/fsi'
import { useServiceContext } from '../../root'
import ValidDocIndicator from '../../components/shared/ValidDocIndicator'
import { DocumentTableMenu } from './DocumentTableMenu'
import { buildUrlWithCSelections } from '~/utils/navigation'

interface FsiGridItemProps {
  row: any
  viewType?: string
  isCommonFav?: boolean
}

export const FsiGridItem: React.FC<FsiGridItemProps> = ({ row: fsi, viewType, isCommonFav }) => {
  const context = useServiceContext()
  const {
    service: {
      filter: { translate, date }
    }
  } = context
  const navigate = useNavigate()

  const excerpt = (text: string, withDots: boolean = false, length: number = 26) => {
    if (!text) return ''
    if (text.length <= length) return text
    return text.substring(0, length) + (withDots ? '...' : '')
  }

  const getThumbnailUrl = (fsi: any, size: number = 256): string => {
    if (!fsi || !fsi.thumbnailId) {
      return '/assets/images/icons/file-default.png'
    }
    const thumbnailBaseUrl = context?.constant?.file?.thumbnailUrl || '/api/files/thumbnail/'
    const tenantUuid = context?.service?.auth?.getTenant?.() ?? ''
    return `${thumbnailBaseUrl}${fsi.thumbnailId}?size=${size}&tenantUuid=${tenantUuid}`
  }

  const handleFolderClick = (e: React.MouseEvent) => {
    if (
      (e as React.MouseEvent<HTMLAnchorElement>).metaKey ||
      (e as React.MouseEvent<HTMLAnchorElement>).ctrlKey ||
      e.button !== 0
    )
      return
    e.preventDefault()
    navigate(`/browser/${fsi.id}`)
  }

  const handleFileClick = (e: React.MouseEvent) => {
    if (
      (e as React.MouseEvent<HTMLAnchorElement>).metaKey ||
      (e as React.MouseEvent<HTMLAnchorElement>).ctrlKey ||
      e.button !== 0
    )
      return
    e.preventDefault()
    navigate(`/show-file/${fsi.id}`)
  }

  if (fsi.type === FsiType.FOLDER) {
    return (
      <div className="d-flex flex-wrap folder-item">
        <a
          href={buildUrlWithCSelections(`/browser/${fsi.id}`)}
          onClick={handleFolderClick}
          title={fsi.name}
          className="align-left"
        >
          <i className="icon-folder" />
          {excerpt(fsi.name, false, 26)}
        </a>
        {!isCommonFav && (
          <div className="align-right actions">
            <DocumentTableMenu item={fsi} options={{}} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="grid-item-wrapper">
      {fsi.type === FsiType.REFERENCE && (
        <img src="/assets/images/icons/reference_badge.svg" className="wf-badge" alt="" />
      )}
      {(fsi.workflow || (fsi.inWorkflow && fsi.showWorkflowIcon)) && (
        <img src="/assets/images/icons/wf_badge.svg" className="wf-badge" alt="" />
      )}
      {fsi.type === FsiType.PLACEHOLDER ? (
        <div className="thumb-bg placeholder-icon-bg" />
      ) : fsi.acl !== AclLevel.BROWSE && fsi.valid ? (
        <a
          href={buildUrlWithCSelections(`/show-file/${fsi.id}`)}
          onClick={handleFileClick}
          className="thumb-bg"
          style={{
            backgroundImage: `url(${getThumbnailUrl(fsi)})`
          }}
        />
      ) : (
        <div
          className="thumb-bg"
          style={{
            backgroundImage: `url(${getThumbnailUrl(fsi)})`
          }}
        />
      )}
      {fsi.acl !== AclLevel.BROWSE && fsi.valid && fsi.type !== FsiType.PLACEHOLDER ? (
        <a href={buildUrlWithCSelections(`/show-file/${fsi.id}`)} onClick={handleFileClick} className="file-info">
          <strong className="filename" title={fsi.name}>
            <ValidDocIndicator fsi={fsi} />
            {excerpt(fsi.name, false, 26)}
          </strong>
        </a>
      ) : (
        <div className="file-info">
          <strong className="filename" title={fsi.name}>
            <ValidDocIndicator fsi={fsi} />
            {excerpt(fsi.name, false, 26)}
          </strong>
        </div>
      )}
      <div className="actions-wrapper">
        {viewType === 'periodicReviewUpcoming' ? (
          <div style={{ display: 'inline-block' }}>
            <span className="created">
              {translate('NEXT_PERIODIC_REVIEW_DATE_THUMBNAIL', { date: date(fsi.nextPeriodicReviewDate) })}
            </span>
          </div>
        ) : (
          <div style={{ display: 'inline-block' }}>
            <span className="created">{translate('UPDATED_ONDATE', { date: date(fsi.updatedOn) })}</span>
          </div>
        )}
        <div className="actions" style={{ display: 'inline-block', float: 'right' }}>
          {fsi.valid && !isCommonFav && (
            <DocumentTableMenu
              item={fsi}
              options={{
                parentFolderId: fsi?.id
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
